// API service layer as specified in appmark.txt
import axios, { AxiosInstance, AxiosResponse } from "axios";
import {
  DashboardOverview,
  WorkflowStatus,
  AgentStatus,
  ApprovalRequest,
  SystemHealth,
  AuthResponse,
  LogEntry,
  Pipeline,
  Artifact,
  WorkspaceEntry,
} from "../types";
import { ProcessorMetadata } from "../types/processor";

class ApiService {
  private client: AxiosInstance;
  private baseURL: string;

  constructor() {
    // Configuration as specified in appmark
    // Use environment variables from Docker Compose if available
    const apiUrl = process.env.REACT_APP_API_URL;

    // Prioritize REACT_APP_API_URL from Docker environment
    if (apiUrl) {
      this.baseURL = `${apiUrl}/api`;
    } else if (process.env.NODE_ENV === "production") {
      this.baseURL = "https://api.roboworks.com/api";
    } else {
      this.baseURL = "http://localhost:8000/api";
    }

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add auth token to requests
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle auth and authorization errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("auth_token");
          window.location.href = "/login";
        }
        if (error.response?.status === 403) {
          const detail = error.response?.data?.detail || "Permission denied";
          const event = new CustomEvent("rbac-denied", { detail });
          window.dispatchEvent(event);
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication endpoints
  async login(username: string, password: string): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.client.post(
      "/auth/login",
      {
        username,
        password,
      }
    );
    return response.data;
  }

  async getCurrentUser(): Promise<{ username: string; role: string }> {
    const response = await this.client.get("/auth/me");
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.client.post("/auth/logout");
    } finally {
      localStorage.removeItem("auth_token");
    }
  }

  // Dashboard endpoints as specified in appmark
  async getDashboardOverview(filter?: string): Promise<DashboardOverview> {
    const params = filter ? { filter } : {};
    const response: AxiosResponse<DashboardOverview> = await this.client.get(
      "/dashboard/overview",
      { params }
    );
    return response.data;
  }

  async getActiveWorkflows(filter?: string): Promise<WorkflowStatus[]> {
    const params = filter ? { filter } : {};
    const response: AxiosResponse<WorkflowStatus[]> = await this.client.get(
      "/workflows/active",
      { params }
    );
    return response.data;
  }

  async getWorkflowDetail(workflowId: string): Promise<WorkflowStatus> {
    const response: AxiosResponse<WorkflowStatus> = await this.client.get(
      `/workflows/${workflowId}`
    );
    return response.data;
  }

  async getWorkflowLogs(workflowId: string): Promise<LogEntry[]> {
    const response: AxiosResponse<LogEntry[]> = await this.client.get(
      `/workflows/${workflowId}/logs`
    );
    return response.data;
  }

  async pauseWorkflow(workflowId: string): Promise<void> {
    await this.client.post(`/workflows/${workflowId}/pause`);
  }

  async resumeWorkflow(workflowId: string): Promise<void> {
    await this.client.post(`/workflows/${workflowId}/resume`);
  }

  async changeWorkflowPriority(
    workflowId: string,
    priority: string
  ): Promise<void> {
    await this.client.put(`/workflows/${workflowId}/priority`, { priority });
  }

  async archiveWorkflow(workflowId: string): Promise<void> {
    await this.client.post(`/workflows/${workflowId}/archive`);
  }

  async updateWorkflow(
    workflowId: string,
    data: {
      name?: string;
      specification_content?: string;
      pipeline_id?: number | null;
      repository_name?: string;
      workflow_inputs?: string; // JSON string
      new_files?: string; // JSON string
      delete_files?: string; // JSON string
    } | FormData
  ): Promise<void> {
    // If already FormData, use it directly (allows file uploads from frontend)
    let formData: FormData;
    if (data instanceof FormData) {
      formData = data;
    } else {
      // Backend expects FormData, not JSON - construct from data object
      formData = new FormData();
      if (data.name) formData.append('name', data.name);
      if (data.specification_content) formData.append('specification_content', data.specification_content);
      if (data.pipeline_id !== undefined && data.pipeline_id !== null) {
        formData.append('pipeline_id', data.pipeline_id.toString());
      }
      if (data.repository_name) formData.append('repository_name', data.repository_name);
      if (data.workflow_inputs) formData.append('workflow_inputs', data.workflow_inputs);
      if (data.new_files) formData.append('new_files', data.new_files);
      if (data.delete_files) formData.append('delete_files', data.delete_files);
    }
    
    await this.client.put(`/workflows/${workflowId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async resetWorkflow(workflowId: string): Promise<any> {
    return await this.client.post(`/workflows/${workflowId}/reset`);
  }

  async getWorkflowAgentExecutions(workflowId: string): Promise<{
    workflow_id: number;
    processor_executions: Array<{
      id: number;
      workflow_id: number;
      processor_type: string;
      status: string;
      output_data: string;
      started_at: string | null;
      completed_at: string | null;
    }>;
  }> {
    const response = await this.client.get(
      `/workflows/${workflowId}/agent-executions`
    );
    return response.data;
  }

  async getAgentStatus(): Promise<AgentStatus[]> {
    const response: AxiosResponse<AgentStatus[]> = await this.client.get(
      "/agents/status"
    );
    return response.data;
  }

  async getApprovalQueue(): Promise<ApprovalRequest[]> {
    const response: AxiosResponse<ApprovalRequest[]> = await this.client.get(
      "/approvals/queue"
    );
    return response.data;
  }

  async getApprovalDetails(approvalId: string): Promise<any> {
    const response = await this.client.get(`/approvals/${approvalId}`);
    return response.data;
  }

  async getApprovalTraceability(approvalId: string): Promise<any> {
    const response = await this.client.get(
      `/approvals/${approvalId}/traceability`
    );
    return response.data;
  }

  async approveRequest(approvalId: string, comment?: string): Promise<void> {
    await this.client.post(`/approvals/${approvalId}/approve`, {
      comment: comment || "",
    });
  }

  async rejectRequest(approvalId: string, comment: string): Promise<void> {
    await this.client.post(`/approvals/${approvalId}/reject`, { comment });
  }

  async addApprovalComment(approvalId: string, comment: string): Promise<void> {
    await this.client.post(`/approvals/${approvalId}/comment`, { comment });
  }

  async batchApprove(approvalIds: string[], comment?: string): Promise<void> {
    await this.client.post("/approvals/batch/approve", {
      approvalIds,
      comment: comment || "",
    });
  }

  async batchReject(approvalIds: string[], comment: string): Promise<void> {
    await this.client.post("/approvals/batch/reject", { approvalIds, comment });
  }

  async getSystemHealth(): Promise<SystemHealth> {
    const response: AxiosResponse<SystemHealth> = await this.client.get(
      "/system/health"
    );
    return response.data;
  }

  async getLLMProviders(): Promise<Array<{
    id: string;
    name: string;
    type: string;
    model: string;
    enabled: boolean;
    default: boolean;
  }>> {
    const response = await this.client.get("/llm/providers");
    return response.data;
  }

  async createWorkflow(formData: FormData): Promise<{
    message: string;
    workflow_id: number;
    traceability_id: string;
    repository_url: string;
    spec_files_count: number;
    status: string;
  }> {
    const response = await this.client.post("/workflows/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }

  async getPipelines(params?: {
    status?: string;
    created_by?: string;
  }): Promise<Pipeline[]> {
    const response = await this.client.get<Pipeline[]>("/pipelines/", {
      params,
    });
    return response.data;
  }

  async getPipeline(id: number): Promise<Pipeline> {
    const response = await this.client.get<Pipeline>(`/pipelines/${id}`);
    return response.data;
  }

  async createPipeline(data: {
    name: string;
    description?: string;
    version: string;
    status?: string;
    steps: Array<{
      step_id: string;
      processor_name: string;
      config: Record<string, any>;
      depends_on: string[];
      output_keys: string[];
    }>;
  }): Promise<Pipeline> {
    const response = await this.client.post<Pipeline>("/pipelines/", data);
    return response.data;
  }

  async updatePipeline(
    id: number,
    data: {
      name?: string;
      description?: string;
      version?: string;
      status?: string;
      steps?: Array<{
        step_id: string;
        processor_name: string;
        config: Record<string, any>;
        depends_on: string[];
        output_keys: string[];
      }>;
    }
  ): Promise<Pipeline> {
    const response = await this.client.put<Pipeline>(`/pipelines/${id}`, data);
    return response.data;
  }

  async getPipelineSteps(pipelineId: number): Promise<{
    id: number;
    name: string;
    version: string;
    status: string;
    description: string | null;
    steps: Array<{
      step_id: string;
      processor_name: string;
      name: string;
      order: number;
      config: Record<string, any>;
      on_fail: string;
      ui_position: { x: number; y: number };
      input_artifact_keys: Record<string, any>;
    }>;
    created_at: string | null;
    updated_at: string | null;
  }> {
    const response = await this.client.get(`/pipeline/${pipelineId}/steps`);
    return response.data;
  }

  async getProcessors(): Promise<ProcessorMetadata[]> {
    const response = await this.client.get<ProcessorMetadata[]>("/processors");
    return response.data;
  }

  async deletePipeline(id: number): Promise<void> {
    await this.client.delete(`/pipelines/${id}`);
  }

  async duplicatePipeline(id: number): Promise<Pipeline> {
    const response = await this.client.post<Pipeline>(`/pipelines/${id}/duplicate`);
    return response.data;
  }

  async validatePipeline(data: {
    steps: any[];
    name?: string;
  }): Promise<{
    is_valid: boolean;
    errors: Array<{
      type: string;
      step_id?: string;
      message: string;
    }>;
    warnings: Array<{
      type: string;
      step_id?: string;
      message: string;
    }>;
  }> {
    const response = await this.client.post('/pipelines/validate', data);
    return response.data;
  }

  async exportPipeline(id: number): Promise<Blob> {
    const response = await this.client.get(`/pipelines/${id}/export`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async importPipeline(file: File): Promise<Pipeline> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post<Pipeline>('/pipelines/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // A2A Agent endpoints (SWRM-134)
  async getA2AAgents(): Promise<{
    total: number;
    agents: Array<{
      name: string;
      slug: string;
      description: string;
      version: string;
      agent_card_url: string;
      tasks_endpoint: string;
    }>;
  }> {
    const response = await this.client.get('/a2a/agents');
    return response.data;
  }

  async publishA2AAgent(pipelineId: number, customSlug?: string | null): Promise<void> {
    await this.client.post(`/pipelines/${pipelineId}/publish-a2a`, {
      slug: customSlug,
    });
  }

  async unpublishA2AAgent(slug: string): Promise<void> {
    await this.client.post(`/a2a/agents/${slug}/unpublish`);
  }

  // API Key Management endpoints (SWRM-134)
  async getAPIKeys(): Promise<Array<{
    id: string;
    key_prefix: string;
    name: string;
    created_by: number | null;
    created_at: string;
    last_used_at: string | null;
    is_active: boolean;
  }>> {
    const response = await this.client.get('/api-keys');
    return response.data;
  }

  async createAPIKey(data: { name: string; scopes?: Record<string, any> }): Promise<{
    id: string;
    key_prefix: string;
    name: string;
    created_by: number | null;
    created_at: string;
    is_active: boolean;
    api_key: string;
  }> {
    const response = await this.client.post('/api-keys', data);
    return response.data;
  }

  async revokeAPIKey(keyId: string): Promise<void> {
    await this.client.delete(`/api-keys/${keyId}`);
  }

  async getAPIKey(keyId: string): Promise<{
    id: string;
    key_prefix: string;
    name: string;
    created_by: number | null;
    created_at: string;
    last_used_at: string | null;
    is_active: boolean;
  }> {
    const response = await this.client.get(`/api-keys/${keyId}`);
    return response.data;
  }

  // Human Input endpoints (SWRM-111)
  async submitHumanInput(
    workflowId: string,
    content: string,
    stepId?: string
  ): Promise<any> {
    const formData = new FormData();
    formData.append('content', content);
    if (stepId) {
      formData.append('step_id', stepId);
    }

    const response = await this.client.post(
      `/workflows/${workflowId}/human-input`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async getHumanInputs(
    workflowId: string,
    includeProcessed: boolean = true
  ): Promise<any> {
    const response = await this.client.get(
      `/workflows/${workflowId}/human-inputs`,
      {
        params: {
          include_processed: includeProcessed,
        },
      }
    );
    return response.data;
  }

  async getWorkflowArtifacts(workflowId: string): Promise<Artifact[]> {
    const response = await this.client.get(
      `/workflows/${workflowId}/artifacts`
    );
    // Backend returns flat array of artifacts
    return response.data || [];
  }

  async downloadArtifact(
    workflowId: string,
    stepId: string,
    artifactKey: string
  ): Promise<Blob> {
    const response = await this.client.get(
      `/workflows/${workflowId}/artifacts/${stepId}/${artifactKey}/download`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  async listWorkspaceEntries(
    workflowId: string,
    path: string = ''
  ): Promise<WorkspaceEntry[]> {
    const params = path ? { path } : {};
    const response = await this.client.get<WorkspaceEntry[]>(
      `/workflows/${workflowId}/workspace/entries`,
      { params }
    );
    return response.data ?? [];
  }

  async getWorkspaceFile(workflowId: string, path: string): Promise<Blob> {
    const response = await this.client.get(
      `/workflows/${workflowId}/workspace/files`,
      { params: { path }, responseType: 'blob' }
    );
    return response.data;
  }

  async getWorkflowLLMConversation(workflowId: string): Promise<any> {
    const response = await this.client.get(
      `/workflows/${workflowId}/llm-conversation/current`
    );
    return response.data;
  }

  // ============================================================================
  // Playbook endpoints (SWRM-XXX)
  // ============================================================================

  async listPlaybooks(params?: {
    category?: string;
    search?: string;
    is_public?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{
    playbooks: any[];
    total: number;
  }> {
    const response = await this.client.get('/playbooks', { params });
    return response.data;
  }

  async getPlaybook(id: number): Promise<any> {
    const response = await this.client.get(`/playbooks/${id}`);
    return response.data;
  }

  async createPlaybook(data: {
    title: string;
    description: string;
    markdown_content: string;
    category: string;
    tags?: string[];
  }): Promise<any> {
    const response = await this.client.post('/playbooks', data);
    return response.data;
  }

  async updatePlaybook(
    id: number,
    data: {
      title?: string;
      description?: string;
      markdown_content?: string;
      category?: string;
      tags?: string[];
    }
  ): Promise<any> {
    const response = await this.client.put(`/playbooks/${id}`, data);
    return response.data;
  }

  async publishPlaybook(id: number): Promise<any> {
    const response = await this.client.post(`/playbooks/${id}/publish`);
    return response.data;
  }

  async unpublishPlaybook(id: number): Promise<any> {
    const response = await this.client.delete(`/playbooks/${id}/publish`);
    return response.data;
  }

  async exportPlaybook(id: number): Promise<void> {
    const response = await this.client.get(`/playbooks/${id}/export`, {
      responseType: 'blob',
    });

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `playbook-${id}.json`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Create blob URL and trigger download
    const blob = new Blob([response.data], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async importPlaybook(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post('/playbooks/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // ============================================================================
  // RBAC endpoints (ADR-017)
  // ============================================================================

  async getGroups(): Promise<Array<{
    id: string; name: string; description: string | null;
    parent_id: string | null; created_at: string;
  }>> {
    const response = await this.client.get('/groups/');
    return response.data;
  }

  async getGroupHierarchy(): Promise<any[]> {
    const response = await this.client.get('/groups/hierarchy');
    return response.data;
  }

  async getMyGroups(): Promise<Array<{
    id: string; name: string; description: string | null;
    parent_id: string | null; created_at: string;
  }>> {
    const response = await this.client.get('/groups/my');
    return response.data;
  }

  async createGroup(data: { name: string; description?: string; parent_id?: string }): Promise<any> {
    const response = await this.client.post('/groups/', data);
    return response.data;
  }

  async updateGroup(id: string, data: { name?: string; description?: string; parent_id?: string }): Promise<any> {
    const response = await this.client.put(`/groups/${id}`, data);
    return response.data;
  }

  async deleteGroup(id: string): Promise<void> {
    await this.client.delete(`/groups/${id}`);
  }

  async getGroupRoleAssignments(groupId: string): Promise<Array<{
    assignment_id: string; user_id: number; username: string;
    role_id: string; role_name: string; scope_type: string;
  }>> {
    const response = await this.client.get(`/groups/${groupId}/role-assignments`);
    return response.data;
  }

  async getRoles(): Promise<Array<{
    id: string; name: string; description: string | null;
    is_system_role: boolean; permissions: string[]; user_count: number; created_at: string;
  }>> {
    const response = await this.client.get('/roles/');
    return response.data;
  }

  async createRole(data: { name: string; description?: string; permissions: string[] }): Promise<any> {
    const response = await this.client.post('/roles/', data);
    return response.data;
  }

  async updateRole(id: string, data: { name?: string; description?: string; permissions?: string[] }): Promise<any> {
    const response = await this.client.put(`/roles/${id}`, data);
    return response.data;
  }

  async deleteRole(id: string): Promise<void> {
    await this.client.delete(`/roles/${id}`);
  }

  async getPermissions(): Promise<Array<{
    resource: string; actions: string[]; category: string;
  }>> {
    const response = await this.client.get('/permissions/');
    return response.data;
  }

  async getUserRoles(userId: number): Promise<Array<{
    id: string; user_id: number; role_id: string; role_name: string | null;
    scope_type: string; scope_id: string | null;
    assigned_by: string | null; assigned_at: string;
  }>> {
    const response = await this.client.get(`/users/${userId}/roles`);
    return response.data;
  }

  async assignUserRole(userId: number, data: {
    role_id: string; scope_type: string; scope_id?: string;
  }): Promise<any> {
    const response = await this.client.post(`/users/${userId}/roles`, data);
    return response.data;
  }

  async removeUserRole(userId: number, assignmentId: string): Promise<void> {
    await this.client.delete(`/users/${userId}/roles/${assignmentId}`);
  }

  async searchUsers(query: string): Promise<Array<{
    id: number; username: string; email: string | null; full_name: string | null;
  }>> {
    const response = await this.client.get('/users/search', { params: { q: query } });
    return response.data;
  }

  async getUsers(limit?: number, offset?: number): Promise<Array<{
    id: number; username: string; email: string | null; full_name: string | null;
  }>> {
    const response = await this.client.get('/users/', { params: { limit, offset } });
    return response.data;
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
